import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/admin-supabase'

interface User {
  id: string
  email: string
  nome: string
  role: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  isAuthenticated: boolean
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(false) // Começa como false para não bloquear

  useEffect(() => {
    // Verificar sessão atual
    const getSession = async () => {
      try {
        const storedUser = localStorage.getItem('admin_user')
        
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser)
            setUser(userData)
          } catch (error) {
            localStorage.removeItem('admin_user')
          }
        }
      } catch (error) {
        console.error('Erro ao verificar sessão:', error)
      } finally {
        setLoading(false)
      }
    }

    getSession()
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true)
      
      // Validação básica
      if (!email || !password) {
        throw new Error('Preencha todos os campos')
      }

      if (email.length < 2) {
        throw new Error('Digite um nome de usuário válido')
      }

      if (password.length < 3) {
        throw new Error('A senha deve ter pelo menos 3 caracteres')
      }
      
      // Autenticar usando função simplificada
      const { data, error } = await supabase.rpc('autenticar_usuario_simples', {
        p_email: email,
        p_senha: password
      })

      if (error) {
        console.error('Erro na autenticação:', error)
        throw new Error('Erro ao conectar com o banco de dados')
      }

      if (!data || data.length === 0) {
        throw new Error('Email ou senha incorretos')
      }

      const userData = data[0]

      if (userData.role !== 'admin') {
        throw new Error('Acesso negado. Apenas administradores podem acessar o painel.')
      }

      setUser(userData)
      localStorage.setItem('admin_user', JSON.stringify(userData))
      
      alert('Login realizado com sucesso!')
    } catch (error: any) {
      console.error('Erro no signIn:', error)
      alert(error.message || 'Erro ao fazer login')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setUser(null)
      localStorage.removeItem('admin_user')
      alert('Logout realizado com sucesso!')
    } catch (error: any) {
      alert(error.message || 'Erro ao fazer logout')
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signOut,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
